// Verbindung zu Smart Royalty Systems direkt aus dem Browser – mit der SRS-Sitzung des Nutzers.
// Gleiche Wege wie die SRS-App: Anmeldung (/auth/login, /auth/code) und Tabellen-Schnittstelle.
// Die Adresse kommt vom Studio-Server (/health → login.srsApi), im Probebetrieb die Test-API.
import { useStudio } from '../state/store.ts';
import { gemaSplits } from './werkbeteiligung.js';

let basis = '';
export const setSrsBasis = (url: string) => { basis = url.replace(/\/+$/, ''); };
export const srsBasis = () => basis;
export const istTestsystem = () => /\/test\/v1$/.test(basis);

// Teams-/Datenbank-IDs aus der Ninox-Zeit; der Server ignoriert sie (wie konfig.js in SRS)
const TABELLEN = '/teams/6d9SmRdHiif5hRKMn/databases/l8h83428yj18/tables';

async function srs<T>(pfad: string, init: RequestInit = {}, mitSitzung = true): Promise<T> {
  if (!basis) throw new Error('SRS ist nicht angebunden');
  const sitzung = useStudio.getState().srsSitzung;
  const res = await fetch(basis + pfad, {
    ...init,
    headers: { 'content-type': 'application/json', ...(mitSitzung && sitzung ? { authorization: `Bearer ${sitzung}` } : {}), ...init.headers },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401 && mitSitzung) throw Object.assign(new Error('Die SRS-Anmeldung ist abgelaufen. Bitte neu anmelden.'), { status: 401 });
    throw Object.assign(new Error((body as { message?: string }).message || `SRS antwortet mit ${res.status}`), { status: res.status });
  }
  return body as T;
}

// ---- Anmeldung (Passwort + 6-stelliger Code per Mail) ----
export const srsLogin = (email: string, passwort: string) =>
  srs<{ vorgang: string; gueltigMinuten: number }>('/auth/login', { method: 'POST', body: JSON.stringify({ email, passwort }) }, false);
export const srsCode = (vorgang: string, code: string) =>
  srs<{ token: string; benutzer: { id: number; name: string; email: string; role: string; mandant: string } }>('/auth/code', { method: 'POST', body: JSON.stringify({ vorgang, code }) }, false);
export const srsAbmelden = () => srs('/auth/abmelden', { method: 'POST' }).catch(() => undefined);

// ---- Tabellen ----
type Zeile = { id: number | string; fields: Record<string, unknown> };
export async function ladeAlle(tabelle: string): Promise<Zeile[]> {
  const alle: Zeile[] = [];
  for (let seite = 0; seite < 50; seite++) {
    const rows = await srs<Zeile[]>(`${TABELLEN}/${tabelle}/records?perPage=1000&page=${seite}`);
    alle.push(...rows);
    if (rows.length < 1000) break;
  }
  return alle;
}
async function schreibe(tabelle: string, eintraege: { id?: number | string; fields: Record<string, unknown> }[]) {
  const erg: Zeile[] = [];
  for (let i = 0; i < eintraege.length; i += 50) {
    erg.push(...(await srs<Zeile[]>(`${TABELLEN}/${tabelle}/records`, { method: 'POST', body: JSON.stringify(eintraege.slice(i, i + 50)) })));
  }
  return erg;
}

// ---- Namen, IPI und Verlage aus dem Katalog (für das Split-Sheet) ----
export interface Katalog { ipi: Map<string, string>; verlage: string[]; namen: string[] }
export async function ladeKatalog(): Promise<Katalog> {
  const bet = await ladeAlle('Song_Beteiligte');
  const ipi = new Map<string, string>(), verlage = new Set<string>(), namen = new Set<string>();
  for (const b of bet) {
    const n = String(b.fields.Name ?? '').trim();
    if (!n) continue;
    if (/verlag|verleger|publisher/i.test(String(b.fields.Rolle ?? ''))) verlage.add(n); else namen.add(n);
    const i = String(b.fields.IPI_Nr ?? '').replace(/\D/g, '');
    if (i && !ipi.has(n.toLowerCase())) ipi.set(n.toLowerCase(), i);
  }
  return { ipi, verlage: [...verlage].sort(), namen: [...namen].sort() };
}

// ---- Werk anlegen wie „Werk anlegen“ in der SRS-Werkanmeldung ----
// spec je Urheber: n Name, r 'K' Komponist | 'T' Textdichter, wb Anteil an Musik bzw. Text (%), v Verlag
export interface UrheberSpec { n: string; r: 'K' | 'T'; wb: number; v: string; ipi?: string }
const ROLLE_DB = { K: 'Composer', T: 'Author' } as const;
const OHNE_VERLAG = 'Copyright Control';

/** Zeilen für Song_Beteiligte nach GEMA-Regel (gleiche Logik wie splitZeilen in SRS-Probe/werkanmeldung/modell.js) */
export function splitZeilen(spec: UrheberSpec[], aufteilung: '64/36' | '50/50') {
  const g = gemaSplits(spec.map((u) => ({ name: u.n, rolle: u.r, anteil: u.wb, verlegt: !!u.v && u.v !== OHNE_VERLAG, verlage: [{ name: u.v, anteil: 100 }] })), { aufteilung }) as
    { aufteilung: string; zeilen: { i: number; vi?: number; name: string; verlag?: string; rolle: string; ar: number; vr: number }[] };
  return g.zeilen.map((z) => {
    const u = spec[z.i];
    return z.rolle === 'Originalverlag'
      ? { name: z.verlag || u.v, rolle: 'Verlag', ar: z.ar, vr: z.vr, code: `S${z.i + 1}-${(z.vi || 0) + 1}` }
      : { name: u.n, rolle: ROLLE_DB[u.r], ar: z.ar, vr: z.vr, ipi: u.ipi,
          codes: g.zeilen.filter((y) => y.i === z.i && y.rolle === 'Originalverlag').map((y) => `S${z.i + 1}-${(y.vi || 0) + 1}`),
          vertretenDurch: u.v && u.v !== OHNE_VERLAG ? u.v : OHNE_VERLAG };
  });
}

export async function werkAnlegen(w: { titel: string; bpm?: number; mandant: string; aufteilung: '64/36' | '50/50'; spec: UrheberSpec[]; kiAnteil: number; projektId: string }) {
  if (!w.spec.length) throw new Error('Keine Urheber mit Anteil');
  const felder: Record<string, unknown> = {
    titel: w.titel.trim(), Mandant: w.mandant, GEMA_Anmeldung: 'neu',
    // Herkunft für die Werkanmeldung: aus dem Studio, handgemacht oder mit KI-Anteil
    Quelle: 'Smart Royalty Studio', Studio_Projekt: w.projektId, KI_Anteil: Math.round(w.kiAnteil * 100),
  };
  if (w.bpm) felder.bpm = w.bpm;
  const [neu] = await schreibe('Songs', [{ fields: felder }]);
  if (!neu?.id) throw new Error('SRS hat keine Werk-ID zurückgegeben.');
  const zeilen = splitZeilen(w.spec, w.aufteilung);
  await schreibe('Song_Beteiligte', zeilen.map((z) => ({ fields: {
    Name: z.name, Rolle: z.rolle, Anteil_Perf: z.ar, Anteil_Mech: z.vr, Songs: [neu.id], Mandant: w.mandant,
    ...('ipi' in z && z.ipi ? { IPI_Nr: z.ipi } : {}),
    ...('code' in z && z.code ? { Beziehung_Codes: z.code } : 'codes' in z && z.codes?.length ? { Beziehung_Codes: z.codes.join('#') } : {}),
    ...(z.rolle !== 'Verlag' && 'vertretenDurch' in z && z.vertretenDurch ? { Vertreten_durch: z.vertretenDurch, Zuordnung_Quelle: 'manuell' } : {}),
  } })));
  return { id: String(neu.id), zeilen };
}
